import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import Svg from 'react-inlinesvg';
import { Grid, Label } from 'semantic-ui-react';
import { string, node, func, array, bool, object } from 'prop-types';
import InputMask from 'react-input-mask';
import DayPicker from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import 'react-day-picker/lib/style.css';

import { Formatters, GridNumbers } from '../../../utils';
import { calendarIcon } from '../../../images';
import StyledSelect from '../Select/Select.style';
import StyledDatepicker, { StyledIcon } from './DateField.style';

// because the business decision is to care only Notrh American date format
// this is static date format used for this component
// please change to dynamic as it goes international
const dateOutputFormat = 'MM/DD/YYYY';
const currentYear = new Date().getFullYear();
const fromMonth = new Date(currentYear, 0);
const toMonth = new Date(currentYear - 50, 11);

/**
 * @component WeekdayElement
 * Sub component used inside of `DateField` calendar
 */
const WeekdayElement = ({ className, weekday, localeUtils, locale }) => {
  const weekdayName = localeUtils.formatWeekdayLong(weekday, locale);
  return (
    <div className={className} title={weekdayName}>
      {weekdayName.slice(0, 1)}
    </div>
  );
};

/**
 * @component YearMonthFields
 * Sub component used inside of `DateField` calendar
 * To share the same static values (fromMonth, toMonth), stays in the same file
 */
const YearMonthFields = ({ date, localeUtils, locale, onChange }) => {
  let month = date.getMonth();
  let year = date.getFullYear();

  const months = localeUtils.getMonths(locale);
  const years = [];
  for (let i = fromMonth.getFullYear(); i >= toMonth.getFullYear(); i -= 1) {
    years.push(i);
  }

  const handleOnChange = () => onChange(new Date(year, month));

  return (
    <div className="DayPicker-Caption">
      <Grid>
        <Grid.Column width={GridNumbers.contentTablet}>
          <StyledSelect className="select">
            <select
              className="ui selection dropdown"
              onChange={e => {
                month = e.target.value;
                handleOnChange();
              }}
              value={date.getMonth()}
            >
              {months.map((option, i) => (
                <option key={option} value={i}>
                  {option}
                </option>
              ))}
            </select>
          </StyledSelect>
        </Grid.Column>

        <Grid.Column width={GridNumbers.asideTablet}>
          <StyledSelect className="select">
            <select
              className="ui selection dropdown"
              onChange={e => {
                year = e.target.value;
                handleOnChange();
              }}
              value={date.getFullYear()}
            >
              {years.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </StyledSelect>
        </Grid.Column>
      </Grid>
    </div>
  );
};

/**
 * @component DateField
 * Main component to be exported
 */
const DateField = ({
  className,
  label,
  error,
  qa,
  mask = '99/99/9999',
  stored = null,
  disabledDays,
  locale = 'en-CA',
  ...rest
}) => {
  const calendar = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [month, setMonth] = useState(fromMonth);

  // querySelector does not work with dots(.) - for e2e
  const id = rest.name && rest.name.replace('.', '_');
  // `stored` is the actual value stored in the form object
  const masked = Formatters.date(stored, dateOutputFormat);
  const fieldCSS = cn('field', className, error && 'error');
  const { onChange, onBlur, ...maskRest } = rest;
  const [lang] = locale.split('-');

  const cleanNum = num => num.replace(/\D/g, '');
  const onInputChange = e => {
    const value = e.target.value;
    const hasValue = cleanNum(value);
    // trigger change event prop function
    onChange(hasValue ? new Date(value) : '');
    // update value state
    setValue(hasValue ? value : '');
    // move calendar month
    if (hasValue) {
      // eslint-disable-next-line no-unused-vars
      const [M, D, Y] = value.split('/');
      setMonth(new Date(cleanNum(Y), Number(cleanNum(M)) - 1));
    }
  };

  const onDayClick = selected => {
    // trigger change event prop function
    onChange(selected);
    // update value state
    setValue(Formatters.date(selected, dateOutputFormat));
    // close calendar
    setIsOpen(false);
  };

  const handleOnBlur = e => {
    if (
      calendar &&
      calendar.current &&
      !calendar.current.contains(e.target) &&
      !isOpen
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    /**
     * when the masked value is set from outside
     * AND not in the middle of typing (regex to look for _)
     * there will be formatted valid date (eg MM/DD/YYYY) in string
     * or empty string for invalid date
     */
    if ((masked || masked === '') && masked !== value && !/_/g.test(value)) {
      setValue(masked);
    }
  }, [stored]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOnBlur);
  }, [calendar]);

  return (
    <div
      className={fieldCSS}
      ref={elm => (calendar.current = elm)}
      data-testid="date-field"
    >
      <label htmlFor={id}>{label}</label>
      <InputMask
        mask={mask}
        onChange={onInputChange}
        onBlur={onBlur}
        value={value}
        {...maskRest}
      >
        {inputProps => (
          <input
            type="tel"
            data-qa={qa}
            id={id}
            {...inputProps}
            {...maskRest}
            placeholder={dateOutputFormat}
          />
        )}
      </InputMask>

      {!rest.disabled && (
        <StyledIcon
          name={isOpen && 'close'}
          color="green"
          onClick={() => setIsOpen(!isOpen)}
        >
          {!isOpen && <Svg src={calendarIcon} />}
        </StyledIcon>
      )}

      {error && (
        <Label basic pointing color="red">
          {error}
        </Label>
      )}

      <StyledDatepicker
        className={cn('datepicker', isOpen && 'datepicker--active')}
      >
        <DayPicker
          month={month}
          selectedDays={stored}
          onDayClick={onDayClick}
          fromMonth={fromMonth}
          toMonth={toMonth}
          disabledDays={disabledDays}
          localeUtils={MomentLocaleUtils}
          locale={lang}
          weekdayElement={<WeekdayElement />}
          captionElement={props => (
            <YearMonthFields {...props} onChange={setMonth} />
          )}
        />
      </StyledDatepicker>
    </div>
  );
};

DateField.propTypes = {
  className: string,
  error: node,
  label: node,
  mask: string,
  stored: object,
  disabledDays: array,
  disabled: bool,
  locale: string,
  onChange: func,
  onBlur: func
};
export default DateField;
